import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CustomModal from "./CustomModal";
import secureStorage from "../../utils/secureStorage";

/**
 * LanguageModal
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - initialLanguage?: string (defaults to current i18n.language)
 */
const LanguageModal = ({ open, onClose, initialLanguage }) => {
  const { i18n, t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    // Normalize language code and provide fallback
    const lang = (initialLanguage || i18n.language || "en").split("-")[0];
    return ["en", "es"].includes(lang) ? lang : "en";
  });

  useEffect(() => {
    if (open) {
      const lang = (initialLanguage || i18n.language || "en").split("-")[0];
      setSelectedLanguage(["en", "sp"].includes(lang) ? lang : "en");
    }
  }, [open, initialLanguage, i18n.language]);

  const languages = [
    { code: "en", name: t("languageModel.english", "English") },
    { code: "sp", name: t("languageModel.spanish", "Spanish") },
  ];

  const handleLanguageChange = (code) => {
    setSelectedLanguage(code);
    try {
      secureStorage.setItem("pendingLanguage", code);
    } catch (e) {
    }
  };

  const applyLanguage = () => {
    i18n
      .changeLanguage(selectedLanguage)
      .catch((e) => {
        // i18n changeLanguage error
      });
    try {
      secureStorage.setItem("i18nextLng", selectedLanguage);
      secureStorage.removeItem("pendingLanguage");
    } catch (e) {
    }
    onClose && onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={t("Setting_page.change_language")}
      variant="primary"
      bodyClass="space-y-6"
    >
      <div
        role="radiogroup"
        aria-label={t("Setting_page.change_language")}
        className="space-y-4"
      >
        {languages.map((lang) => {
          const active = selectedLanguage === lang.code;
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleLanguageChange(lang.code)}
              className="
              w-full 
              bg-[#f4f4f4] dark:bg-[#1f2130] 
              rounded-xl px-5 py-4 
              flex items-center gap-5 
              shadow-sm

              border border-gray-300 dark:border-gray-600
              transition

              focus:outline-none
              focus:ring-2 focus:ring-[#6d29f6]/60
              dark:focus:ring-[#6d29f6]
            "
              role="radio"
              aria-checked={active}
            >
              <span className="relative w-7 h-7 flex items-center justify-center">
                <span
                  className={`w-7 h-7 rounded-full border-2 ${active ? "border-[#6d29f6]" : "border-gray-300"
                    } flex items-center justify-center transition`}
                ></span>
                {active && (
                  <span className="absolute w-4 h-4 rounded-full bg-[#6d29f6]" />
                )}
              </span>
              <span className="font-semibold text-gray-900 dark:text-dark-text">{lang.name}</span>
            </button>
          );
        })}
      </div>
      <div className="pt-2">
        <button
          type="button"
          onClick={applyLanguage}
          className="w-full bg-[#6d29f6] text-white font-medium py-4 rounded-lg tracking-wide shadow-sm active:scale-[0.99]"
        >
          {t("apply", "APPLY")}
        </button>
      </div>
    </CustomModal>
  );
};

export default LanguageModal;
